// src/store/crm/message-templates/index.js
// Conversations message templates: managed in Clinic Settings, consumed by the
// chat composer's template picker.
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { messageTemplatesApi } from "./messageTemplatesApi";
import { sampleTemplates } from "./templateUtils";

export const getMessageTemplates = createAsyncThunk(
  "messageTemplates/list",
  async (params = {}) => {
    try {
      const rows = await messageTemplatesApi.list(params);
      return { rows: Array.isArray(rows) ? rows : [], failed: false };
    } catch (error) {
      // Silent: the composer opens on a click and shouldn't toast a backend
      // outage at the user. The slice falls back to the starter set instead.
      return { rows: [], failed: true };
    }
  }
);

export const createMessageTemplate = createAsyncThunk(
  "messageTemplates/create",
  async (payload) => {
    try {
      const row = await messageTemplatesApi.create(payload);
      toast.success("Template created");
      return row;
    } catch (error) {
      toast.error(error?.message || "Failed to create template");
      return null;
    }
  }
);

export const updateMessageTemplate = createAsyncThunk(
  "messageTemplates/update",
  async ({ id, ...payload }) => {
    try {
      const row = await messageTemplatesApi.update(id, payload);
      toast.success("Template updated");
      return row;
    } catch (error) {
      toast.error(error?.message || "Failed to update template");
      return null;
    }
  }
);

export const deleteMessageTemplate = createAsyncThunk(
  "messageTemplates/delete",
  async ({ id, provider_id }) => {
    try {
      await messageTemplatesApi.remove(id, provider_id ? { provider_id } : undefined);
      toast.success("Template deleted");
      return id;
    } catch (error) {
      toast.error(error?.message || "Failed to delete template");
      return null;
    }
  }
);

export const messageTemplatesSlice = createSlice({
  name: "messageTemplatesStore",
  initialState: {
    templates: [],
    loaded: false,   // a list request has completed at least once
    usingSamples: false, // showing the read-only starter set, not saved rows
    lastRequestFailed: false,
    loading: false,
    saving: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMessageTemplates.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMessageTemplates.fulfilled, (state, action) => {
        const { rows, failed } = action.payload;
        // No saved templates yet (or no endpoint) — show the starter set so the
        // picker and the settings list are never blank. Saving any real
        // template replaces them.
        state.usingSamples = rows.length === 0;
        state.templates = rows.length ? rows : sampleTemplates();
        state.loaded = true;
        state.loading = false;
        state.lastRequestFailed = failed;
      })
      .addCase(createMessageTemplate.pending, (state) => {
        state.saving = true;
      })
      .addCase(createMessageTemplate.fulfilled, (state, action) => {
        state.saving = false;
        if (!action.payload) return;
        const rows = state.usingSamples ? [] : state.templates;
        state.templates = [...rows, action.payload];
        state.usingSamples = false;
      })
      .addCase(updateMessageTemplate.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateMessageTemplate.fulfilled, (state, action) => {
        state.saving = false;
        if (!action.payload) return;
        state.templates = state.templates.map((row) =>
          row.id === action.payload.id ? action.payload : row
        );
      })
      .addCase(deleteMessageTemplate.pending, (state) => {
        state.saving = true;
      })
      .addCase(deleteMessageTemplate.fulfilled, (state, action) => {
        state.saving = false;
        if (!action.payload) return;
        state.templates = state.templates.filter((row) => row.id !== action.payload);
      });
  },
});

export default messageTemplatesSlice.reducer;
